import React, { useEffect, useRef, useState, useMemo } from 'react'
import { Text } from '@react-three/drei'
import { useThree } from '@react-three/fiber'
import * as THREE from 'three'
import { STATUS } from '../../utils/status'

// Utility function to detect touch devices
const isTouchDevice = () => {
  return 'ontouchstart' in window || navigator.maxTouchPoints > 0 || navigator.msMaxTouchPoints > 0
}

export default function Cell({ baseColor, shape, fillOpacity, onHover, onSelect, region, isHighlighted, isHovered, isDimmed, isStageActive, index, register, isLocked = false, isAdminMode = false, activeTab = 'available', activeStage = null, hoveredGroup = null, hoveredRegion = null, selectedGroup = null }) {
  const [hovered, hover] = useState(false)
  const meshRef = useRef()
  const { camera } = useThree()
  const prevActiveStageRef = useRef(activeStage)

  useEffect(() => {
    document.body.style.cursor = hovered ? 'pointer' : 'default'
    return () => {
      document.body.style.cursor = 'default'
    }
  }, [hovered])

  // Clear local hover state when stage changes in admin mode
  useEffect(() => {
    if (isAdminMode) {
      if (activeStage !== prevActiveStageRef.current) {
        hover(false)
        prevActiveStageRef.current = activeStage
      }
      // Also clear hover if activeStage is null (going back to all stages)
      if (!activeStage) {
        hover(false)
      }
    }
  }, [activeStage, isAdminMode])

  useEffect(() => {
    if (meshRef.current && register) register(index, meshRef.current, fillOpacity)
  }, [index, register, fillOpacity])

  // Calculate the center position of the shape for text placement
  const textPosition = useMemo(() => {
    const box = new THREE.Box3().setFromPoints(shape.getPoints())
    const center = new THREE.Vector3()
    box.getCenter(center)
    return center
  }, [shape])

  // Generate text content based on available region data
  const textContent = useMemo(() => {
    if (!region) return ''
    
    const lines = []
    
    // Lot number (always show if available)
    if (region.lotNumber) {
      lines.push(`Lot - ${region.lotNumber}`)
    }
    
    // Lot size (always show if available)
    if (region.lotSize) {
      lines.push(region.lotSize)
    }
    
    return lines.join('\n')
  }, [region])

  // Calculate lot dimensions to constrain text size
  const lotDimensions = useMemo(() => {
    const box = new THREE.Box3().setFromPoints(shape.getPoints())
    const size = new THREE.Vector3()
    box.getSize(size)
    return { width: size.x, height: size.y }
  }, [shape])

  // Calculate responsive text size based on camera zoom and lot size
  const textSize = useMemo(() => {
    const baseSize = 12.0 // Much larger base size for better initial visibility
    const zoomFactor = Math.max(1.0, Math.min(2.5, camera.zoom)) // Scale with zoom, clamp between 1.0 and 2.5
    const calculatedSize = baseSize * zoomFactor
    
    // Constrain text size to fit within lot boundaries
    const maxWidth = lotDimensions.width * 0.9 // Use 90% of lot width
    const maxHeight = lotDimensions.height * 0.8 // Use 80% of lot height
    const maxSize = Math.min(maxWidth, maxHeight)
    
    // When text hits boundaries, it should reduce size as zoom increases
    if (calculatedSize > maxSize) {
      // Reduce size proportionally when exceeding boundaries
      const reductionFactor = maxSize / calculatedSize
      return maxSize * Math.min(1.0, reductionFactor)
    }
    
    return calculatedSize
  }, [camera.zoom, lotDimensions, isStageActive])

  // Only show text if there's content and the region is visible
  const shouldShowText = textContent && region && !isLocked && ( // Hide text for locked stages
    !isStageActive || // Show all when no stage is active
    !isDimmed || // Show when not dimmed
    isHighlighted || // Always show when highlighted
    isHovered || // Always show when hovered
    hovered // Always show when hovered
  )


  return (
    <group>
      <mesh 
        ref={meshRef}
        onPointerOver={(e) => {
          // if (isLocked || isTouchDevice()) return
          hover(true)
          onHover && onHover(region, e.point)
        }} 
        onPointerMove={(e) => {
          // if (isLocked || isTouchDevice()) return
          if (hovered) {
            onHover && onHover(region, e.point)
          }
        }}
        onPointerOut={() => {
          // if (isLocked || isTouchDevice()) return
          hover(false)
          onHover && onHover(null, null)
        }} 
        onClick={(e) => {
          if (isLocked) return 
          e.stopPropagation()
          onSelect && onSelect(region, e.point) 
        }}
      >
        <meshBasicMaterial 
          color={(
            () => {
              // CRITICAL: In admin mode, lots from other stages should always be gray (dimmed)
              if (isAdminMode && isStageActive) {
                const regionStageName = (region.name && String(region.name).trim()) || 'Ungrouped'
                if (regionStageName !== activeStage) {
                  // This lot is from a different stage - always show gray (dimmed)
                  return '#9ca3af' // light gray for lots from other stages
                }
              }
              
              // In admin mode, colors are handled in Svg.js
              // Cell.js just uses the baseColor passed from Svg.js
              // When a group is selected, Svg.js sets the correct colors (sub-group colors for sold lots)
              // We should not override these colors here
              
              // When a stage is active, show status color for active stage lots, blurred gray for others
              if (isStageActive) {
                if (isDimmed) {
                  return '#9ca3af' // light gray for lots from other stages (blurred effect) - always gray
                }
                // Return baseColor as-is - Svg.js has already set the correct color (sub-group color when group selected)
                return baseColor
              }
              // When no stage is active, enhance color for highlighted lots (from sidebar hover)
              if (isHighlighted && baseColor) {
                // Brighten the color by mixing with white (20% white, 80% original)
                const hex = baseColor.replace('#', '')
                const r = parseInt(hex.substr(0, 2), 16)
                const g = parseInt(hex.substr(2, 2), 16)
                const b = parseInt(hex.substr(4, 2), 16)
                // Mix with white (255, 255, 255) at 20% to brighten
                const r2 = Math.round(r * 0.8 + 255 * 0.2)
                const g2 = Math.round(g * 0.8 + 255 * 0.2)
                const b2 = Math.round(b * 0.8 + 255 * 0.2)
                return `#${r2.toString(16).padStart(2, '0')}${g2.toString(16).padStart(2, '0')}${b2.toString(16).padStart(2, '0')}`
              }
              return baseColor // normal status color when no stage is active
            }
          )()} 
          opacity={(
            () => {
              const base = typeof fillOpacity === 'number' ? fillOpacity : 0.2
              
              if (isLocked) {
                // Locked stages should be highly opaque to hide underlying lot details
                return 0.95
              }
              const isActive = (isHighlighted || isHovered || hovered)
              
              // In admin mode when a stage is active
              if (isAdminMode && isStageActive) {
                // CRITICAL: Lots from other stages should always be dimmed (grayed out) but visible
                const regionStageName = (region.name && String(region.name).trim()) || 'Ungrouped'
                if (regionStageName !== activeStage) {
                  // This lot is from a different stage - show it dimmed (grayed out) like in normal mode
                  // Always visible with reduced opacity, similar to normal viewer mode
                  return Math.min(0.85, base)
                }
                
                // Check hover states - only consider lot hover (not group hover)
                const hasLotHover = (hoveredRegion && region && hoveredRegion.id === region.id) || hovered
                
                // Show all lots with reduced opacity when stage is active to keep map details visible
                if (hasLotHover) {
                  // Lot is hovered - show with slightly higher opacity
                  if (isActive) {
                    return Math.min(0.55, base)
                  }
                  return Math.min(0.4, base)
                } else {
                  // No hover - show with lower opacity to keep map details visible
                  return Math.min(0.85, base)
                }
              }
              
              // Normal mode: When a stage is active
              if (isStageActive) {
                if (isDimmed) {
                  // Other stage lots: always visible but with reduced opacity
                  return Math.min(0.85, base)
                } else {
                  // Active stage lots: sold lots should be visible by default, others only on hover/click
                  const isSold = region && region.status === STATUS.SOLD
                  if (isSold) {
                    // Sold lots: always visible with status color but reduced opacity to keep map details visible
                    return Math.min(0.85, base)
                  } else {
                    // Non-sold lots: only visible on hover/click
                    if (!isActive) return 0.0
                    return Math.min(0.55, base)
                  }
                }
              }
              
              // When no stage is active, show status colors by default (visible)
              // This applies to both normal and admin modes
              // Make highlighted lots (from sidebar hover) more visible
              if (isHighlighted) {
                return Math.min(1.0, base * 1.5) // Increase opacity for highlighted lots
              }
              return Math.min(0.85, base)
            }
          )()} 
          depthWrite={false} 
          transparent 
          polygonOffset
          polygonOffsetFactor={-0.5}
        />
        {false && isStageActive && isDimmed && !hovered && !isHighlighted && !isHovered && (
          <meshBasicMaterial 
            color="#ffffff" 
            opacity={0.02} 
            transparent 
            depthWrite={false}
            polygonOffset
            polygonOffsetFactor={-1}
          />
        )}
        <shapeBufferGeometry args={[shape]} />
      </mesh>

      {/* Text overlay showing lot details */}
      {false && shouldShowText && (
        <group scale={[1, -1, 1]}>
          <Text
            position={[textPosition.x, -textPosition.y, 0.1]} // Invert Y position to compensate for counter-inversion
            fontSize={textSize}
            color="#000000"
            anchorX="center"
            anchorY="middle"
            maxWidth={200}
            textAlign="center"
            outlineWidth={0.02}
            outlineColor="#ffffff"
            fontWeight="bold"
          >
            {textContent}
          </Text>
        </group>
      )}
      
      
      {/* Debug: Add a visible sphere to test positioning */}
      {false && region && (
        <mesh position={[0, 0, 0.5]}>
          <sphereGeometry args={[0.3]} />
          <meshBasicMaterial color="#00ff00" />
        </mesh>
      )}
    </group>
  )
}